// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { Appointment, CarePlan, Patient, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  activeTreatmentSearchParams,
  carePlanToTreatment,
  isTestPatient,
  type PatientTreatment,
  SELF_PAY_TAG_SYSTEM,
  TEST_PATIENT_TAG_CODE,
} from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

import { localDateString } from '../utils/patient-treatments';

const TEST_PATIENT_TAG = `${SELF_PAY_TAG_SYSTEM}|${TEST_PATIENT_TAG_CODE}`;
/** Appointments fetched per page of patients; generous enough to cover each patient's history and schedule. */
const APPOINTMENTS_PER_PATIENT = 50;

export type TreatmentMaintenanceSort = 'asc' | 'desc';

export interface TreatmentMaintenanceAppointment {
  date: string;
  type: string;
}

export interface TreatmentMaintenanceEntry {
  id: string;
  patientId: string;
  patientName: string;
  patientSubtitle: string;
  treatmentStarted: string;
  modality: string;
  modalityDetail: string | undefined;
  lastAppointment: TreatmentMaintenanceAppointment | undefined;
  nextAppointment: TreatmentMaintenanceAppointment | undefined;
  isTestPatient: boolean;
}

function getAge(birthDate: string | undefined): number | undefined {
  if (!birthDate) {
    return undefined;
  }
  const date = new Date(birthDate);
  if (!Number.isFinite(date.getTime())) {
    return undefined;
  }
  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  if (now.getMonth() < date.getMonth() || (now.getMonth() === date.getMonth() && now.getDate() < date.getDate())) {
    age -= 1;
  }
  return age;
}

function getAppointmentPatientId(appointment: Appointment): string | undefined {
  return appointment.participant
    ?.find((participant) => participant.actor?.reference?.startsWith('Patient/'))
    ?.actor?.reference?.replace('Patient/', '');
}

function getAppointmentType(appointment: Appointment): string {
  return (
    appointment.appointmentType?.text ??
    appointment.appointmentType?.coding?.find((coding) => coding.display)?.display ??
    appointment.appointmentType?.coding?.[0]?.code ??
    'Appointment'
  );
}

function mapPatient(patient: Patient, treatment: PatientTreatment): TreatmentMaintenanceEntry {
  const name = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient';
  const age = getAge(patient.birthDate);
  const subtitle = [age, patient.gender ? `${patient.gender[0].toUpperCase()}${patient.gender.slice(1)}` : undefined]
    .filter((value): value is string | number => value !== undefined)
    .map(String)
    .join(' - ');
  return {
    id: `${patient.id}|${treatment.id}`,
    patientId: patient.id ?? '',
    patientName: name,
    patientSubtitle: subtitle,
    treatmentStarted: treatment.start,
    modality: treatment.modality,
    modalityDetail: treatment.other ?? treatment.orderName,
    lastAppointment: undefined,
    nextAppointment: undefined,
    isTestPatient: isTestPatient(patient),
  };
}

function attachAppointments(
  entries: TreatmentMaintenanceEntry[],
  appointments: Appointment[],
  now: Date
): TreatmentMaintenanceEntry[] {
  const byPatient = new Map<string, Appointment[]>();
  for (const appointment of appointments) {
    const patientId = getAppointmentPatientId(appointment);
    if (
      !patientId ||
      !appointment.start ||
      appointment.status === 'cancelled' ||
      appointment.status === 'entered-in-error'
    ) {
      continue;
    }
    byPatient.set(patientId, [...(byPatient.get(patientId) ?? []), appointment]);
  }
  return entries.map((entry) => {
    const patientAppointments = (byPatient.get(entry.patientId) ?? []).sort((a, b) =>
      (a.start ?? '').localeCompare(b.start ?? '')
    );
    const past = patientAppointments.filter((appointment) => new Date(appointment.start ?? 0) <= now).at(-1);
    const future = patientAppointments.find((appointment) => new Date(appointment.start ?? 0) > now);
    return {
      ...entry,
      lastAppointment: past?.start ? { date: past.start, type: getAppointmentType(past) } : undefined,
      nextAppointment: future?.start ? { date: future.start, type: getAppointmentType(future) } : undefined,
    };
  });
}

/**
 * Search params for one page of active treatment CarePlans, with their Patients included. Filtering, search, sorting
 * and paging all run server-side so the queue never loads the whole Patient list.
 */
export function buildTreatmentMaintenanceSearchParams({
  page,
  pageSize,
  search,
  hideTestPatients,
  sort,
  today,
}: {
  page: number;
  pageSize: number;
  search?: string;
  hideTestPatients?: boolean;
  sort: TreatmentMaintenanceSort;
  today: string;
}): URLSearchParams {
  const params = activeTreatmentSearchParams(today);
  params.set('_include', 'CarePlan:subject');
  if (search?.trim()) {
    params.set('subject:Patient.name', search.trim());
  }
  if (hideTestPatients) {
    params.append('subject:Patient._tag:not', TEST_PATIENT_TAG);
  }
  params.set('_sort', sort === 'asc' ? 'date' : '-date');
  params.set('_count', String(pageSize));
  params.set('_offset', String((page - 1) * pageSize));
  params.set('_total', 'accurate');
  return params;
}

export function useTreatmentMaintenanceQueue({
  page,
  pageSize,
  search,
  hideTestPatients,
  sort = 'asc',
}: {
  page: number;
  pageSize: number;
  search?: string;
  hideTestPatients?: boolean;
  sort?: TreatmentMaintenanceSort;
}): { entries: TreatmentMaintenanceEntry[]; loading: boolean; total: number } {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<TreatmentMaintenanceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const bundle = await medplum.search(
        'CarePlan',
        buildTreatmentMaintenanceSearchParams({
          page,
          pageSize,
          search,
          hideTestPatients,
          sort,
          today: localDateString(),
        })
      );
      // `_include` adds Patients to the bundle, which Medplum types as CarePlan-only.
      const resources = (bundle.entry ?? []).flatMap((entry): Resource[] => (entry.resource ? [entry.resource] : []));
      const patients = new Map(
        resources
          .filter((resource): resource is Patient => resource.resourceType === 'Patient')
          .map((patient) => [`Patient/${patient.id}`, patient])
      );
      const pageEntries: TreatmentMaintenanceEntry[] = [];
      for (const carePlan of resources.filter(
        (resource): resource is CarePlan => resource.resourceType === 'CarePlan'
      )) {
        const patient = carePlan.subject?.reference ? patients.get(carePlan.subject.reference) : undefined;
        const treatment = carePlanToTreatment(carePlan);
        if (patient && treatment) {
          pageEntries.push(mapPatient(patient, treatment));
        }
      }

      const patientIds = [...new Set(pageEntries.map((entry) => entry.patientId))];
      const appointments =
        patientIds.length > 0
          ? await medplum.searchResources('Appointment', {
              patient: patientIds.map((id) => `Patient/${id}`).join(','),
              _count: String(patientIds.length * APPOINTMENTS_PER_PATIENT),
              // Newest first, so a capped result still holds each patient's upcoming and most recent visits.
              _sort: '-date',
            })
          : [];
      setTotal(bundle.total ?? pageEntries.length);
      setEntries(attachAppointments(pageEntries, appointments, new Date()));
    } finally {
      setLoading(false);
    }
  }, [hideTestPatients, medplum, page, pageSize, search, sort]);

  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);

  return { entries, loading, total };
}
